import { assert } from '@fe/core';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { EventBus } from '../../../app/contexts/event-bus';
import { fix180 } from '../../../common/helpers';
import { AnalyzerRoute } from '../types';
import { RouteAnalyzerEdit } from './route-analyzer-edit';

export class RouteAnalyzerLayer implements FeMapPlugin {
    static readonly CONTEXT_MENU_LAYER = 'fe-route-analyzer-route';

    private map: MaplibreMap | null = null;
    private routes: AnalyzerRoute[] = [];
    private routesSource: GeoJSONSource | null = null;
    private ringsSource: GeoJSONSource | null = null;
    private waypointsSource: GeoJSONSource | null = null;
    private readonly editLayer = new RouteAnalyzerEdit();

    onAdd(map: MaplibreMap) {
        assert(!this.map);
        this.map = map;
        this.map.addSource('fe-route-analyzer-rings', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        this.map.addSource('fe-route-analyzer-route', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        this.map.addSource('fe-route-analyzer-waypoints', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        const slot = 'fe-route-analyzer-slot';

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-route',
                source: 'fe-route-analyzer-route',
                type: 'line',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2
                }
            },
            slot
        );

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-waypoints',
                source: 'fe-route-analyzer-waypoints',
                type: 'symbol',
                layout: {
                    'symbol-placement': 'point',
                    'icon-image': 'fe:triangle-filled',
                    'icon-allow-overlap': true,
                    'icon-size': 0.4
                },
                paint: {
                    'icon-color': '#f0f'
                }
            },
            slot
        );

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-labels',
                source: 'fe-route-analyzer-waypoints',
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'label'],
                    'symbol-placement': 'point',
                    'text-anchor': 'top',
                    'text-allow-overlap': false,
                    'text-optional': true,
                    'text-offset': [0, 0.7],
                    'text-size': 12,
                    'text-font': ['Noto Sans Regular']
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            slot
        );

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-labels-perimeter',
                source: 'fe-route-analyzer-route',
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'label'],
                    'symbol-placement': 'line',
                    'text-anchor': 'top',
                    'text-optional': true,
                    'text-size': 12,
                    'text-font': ['Noto Sans Regular']
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            slot
        );

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-rings',
                source: 'fe-route-analyzer-rings',
                type: 'fill',
                paint: {
                    'fill-color': ['get', 'color']
                }
            },
            slot
        );

        this.map.addLayer(
            {
                id: 'fe-route-analyzer-rings-label',
                source: 'fe-route-analyzer-rings',
                type: 'symbol',
                layout: {
                    'symbol-placement': 'line',
                    'text-anchor': 'top',
                    'text-field': ['get', 'label'],
                    'text-size': 12,
                    'text-font': ['Noto Sans Regular']
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            slot
        );

        this.routesSource = <GeoJSONSource>this.map.getSource('fe-route-analyzer-route');
        this.ringsSource = <GeoJSONSource>this.map.getSource('fe-route-analyzer-rings');
        this.waypointsSource = <GeoJSONSource>this.map.getSource('fe-route-analyzer-waypoints');
        this.editLayer.onAdd(map);
        EventBus.on('route-analyzer-edit-cancel', this.onEditCancel);
    }

    onRemove() {
        assert(this.map);
        EventBus.off('route-analyzer-edit-cancel', this.onEditCancel);
        this.editLayer?.onRemove(this.map);
        this.routesSource = null;
        this.ringsSource = null;
        this.waypointsSource = null;
        this.map = null;
    }

    private onEditCancel = (e: { original: AnalyzerRoute }) => {
        this.add(e.original);
        this.editLayer?.remove();
    };

    setLabels(value: boolean) {
        this.map?.setLayoutProperty('fe-route-analyzer-labels', 'visibility', value ? 'visible' : 'none');
    }

    setWaypoints(value: boolean) {
        this.map?.setLayoutProperty('fe-route-analyzer-waypoints', 'visibility', value ? 'visible' : 'none');
    }

    add(route: AnalyzerRoute) {
        if (!route.waypoints) {
            return;
        }

        this.routes.push(route);
        const coords: number[][][] = [];

        for (let i = 0; i < route.waypoints.items.length - 1; i++) {
            const item = route.waypoints.items[i];
            const nextItem = route.waypoints.items[i + 1];
            let a = [item.ln, item.lt];
            let b = [nextItem.ln, nextItem.lt];

            const distance = turf.distance(a, b, { units: 'nauticalmiles' });
            if (distance > 50) {
                const geometry = turf.greatCircle(a, b, { npoints: Math.floor(distance / 20) }).geometry;
                if (geometry.type === 'LineString') {
                    coords.push(geometry.coordinates);
                } else {
                    coords.push(...geometry.coordinates);
                }
            } else {
                [a, b] = fix180(a, b);
                coords.push([a, b]);
            }
        }

        this.routesSource?.updateData({
            add: [
                {
                    type: 'Feature',
                    id: route.id,
                    geometry: {
                        type: 'MultiLineString',
                        coordinates: coords
                    },
                    properties: {
                        color: route.color,
                        id: route.id,
                        label: route.label
                    }
                }
            ]
        });

        this.ringsSource?.updateData({
            add: route.associatedPoints
                .filter((x) => x.enabled)
                .map((point, i) => ({
                    type: 'Feature',
                    id: route.id + i,
                    geometry: turf.circle([point.longitude, point.latitude], point.range, { units: 'nauticalmiles' })!.geometry,
                    properties: {
                        color: point.color,
                        label: point.label
                    }
                }))
        });

        this.waypointsSource?.updateData({
            add: route.waypoints.items.map((x, i) => ({
                type: 'Feature',
                id: route.id + i,
                geometry: {
                    type: 'Point',
                    coordinates: [x.ln, x.lt]
                },
                properties: {
                    label: x.id
                }
            }))
        });
    }

    removeAll() {
        assert(this.editLayer);
        this.routes = [];
        this.routesSource?.setData(turf.featureCollection([]));
        this.waypointsSource?.setData(turf.featureCollection([]));
        this.editLayer.remove();
        this.ringsSource?.setData(turf.featureCollection([]));
    }

    remove(route: AnalyzerRoute) {
        assert(this.editLayer);
        this.routes = this.routes.filter((x) => x.id !== route.id);

        this.routesSource?.updateData({
            remove: [route.id]
        });

        if (route.waypoints) {
            this.waypointsSource?.updateData({
                remove: route.waypoints.items.map((x, i) => route.id + i)
            });
        }

        if (this.editLayer.getOriginalRoute()?.id === route.id) {
            this.editLayer.remove();
        }

        this.ringsSource?.updateData({
            remove: route.associatedPoints.map((_, i) => route.id + i)
        });
    }

    edit(route: AnalyzerRoute) {
        assert(this.editLayer);
        const original = this.editLayer.getOriginalRoute();
        if (original) {
            this.add(original);
        }
        this.remove(route);
        this.editLayer.edit(route);
    }

    getByIds(ids: string[]): AnalyzerRoute[] {
        return ids.map((x) => this.routes.find((r) => r.id === x)).filter((x) => x) as AnalyzerRoute[];
    }
}
